import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

import { getAuthUserId } from '../services/AuthService';
import {
  loadProgress,
  saveProgress,
  fetchTutorials,
  getTutorialVideoUrl,
  TutorialAudience,
  filterTutorialsByAppAccess,
  getPlayableTutorials,
  buildProgressForTutorialList,
  isProgressCompleteForTutorialCount,
} from '../services/TutorialService';
import { getPatientHasAppAccess } from '../services/PatientService';
import { DoctorRoles, PatientRoles } from '../config/DiactiveTypes';

const Tutorials = ({ role }) => {
  const navigate = useNavigate();
  const userId = getAuthUserId();

  const [videos, setVideos] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videoSrc, setVideoSrc] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [progress, setProgress] = useState([]);
  const [showCompletionStep, setShowCompletionStep] = useState(false);
  const [loading, setLoading] = useState(true);

  const videoRef = useRef(null);

  useEffect(() => {
    const initializeVideos = async () => {
      setLoading(true);
      setShowCompletionStep(false);

      let hasAppAccess = true;
      if (PatientRoles.includes(role)) {
        hasAppAccess = await getPatientHasAppAccess(userId);
      }

      const audience = DoctorRoles.includes(role)
        ? TutorialAudience.Doctor
        : TutorialAudience.Patient;

      const allTutorials = await fetchTutorials({ audience });
      const filteredVideos = getPlayableTutorials(
        filterTutorialsByAppAccess(allTutorials, hasAppAccess)
      );

      setVideos(filteredVideos);

      const savedProgress = await loadProgress(userId);
      const newProgress = buildProgressForTutorialList(
        filteredVideos.length,
        savedProgress
      );
      setProgress(newProgress);

      if (filteredVideos.length > 0) {
        const lastUncompletedIndex = newProgress.indexOf(false);
        if (lastUncompletedIndex >= 0) {
          setCurrentVideoIndex(lastUncompletedIndex);
        }
        else {
          setCurrentVideoIndex(filteredVideos.length - 1);
        }
      }

      setLoading(false);
    };

    initializeVideos();
  }, [userId, role]);

  useEffect(() => {
    const tutorial = videos[currentVideoIndex];
    if (!tutorial?.storagePath) {
      setVideoSrc(null);
      return;
    }

    let cancelled = false;
    setVideoLoading(true);

    getTutorialVideoUrl(tutorial.storagePath)
      .then((url) => {
        if (!cancelled) setVideoSrc(url);
      })
      .catch(() => {
        if (!cancelled) setVideoSrc(null);
      })
      .finally(() => {
        if (!cancelled) setVideoLoading(false);
      });

    return () => { cancelled = true; };
  }, [videos, currentVideoIndex]);

  useEffect(() => {
    if (videoRef.current && videoSrc) {
      videoRef.current.load();
    }
  }, [videoSrc]);

  const handleVideoEnd = () => {
    setProgress((prev) => {
      const updatedProgress = [...prev];
      updatedProgress[currentVideoIndex] = true;
      saveProgress(userId, updatedProgress);
      return updatedProgress;
    });
  };

  const handleNext = () => {
    if (currentVideoIndex < videos.length - 1) {
      setCurrentVideoIndex((prev) => prev + 1);
    }
    else if (isProgressCompleteForTutorialCount(progress, videos.length)) {
      setShowCompletionStep(true);
    }
  };

  const handlePrevious = () => {
    if (showCompletionStep) {
      setShowCompletionStep(false);
      return;
    }

    if (currentVideoIndex > 0) {
      setCurrentVideoIndex((prev) => prev - 1);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (videos.length === 0) {
    return (
      <Container maxWidth="md" sx={{ pt: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              No hay tutoriales disponibles en este momento.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/")}
              sx={{ mt: 3 }}
            >
              Volver al inicio
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ pt: 4 }}>
      <Paper elevation={3} sx={{ padding: 3 }}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ mt: 1, mb: 4 }}>
            {
              !showCompletionStep && (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <Typography variant="h6" gutterBottom>
                        {`${currentVideoIndex + 1}. ${videos[currentVideoIndex].title}`}
                      </Typography>
                      {
                        progress[currentVideoIndex] &&
                        <CheckCircle color="success" sx={{width: 24, mb: 0.8, ml: 0.4}} />
                      }
                    </Box>
                    <Typography variant="body1" gutterBottom>
                      {currentVideoIndex + 1}/{videos.length}
                    </Typography>
                  </Box>
                  {videoLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  )}
                  {!videoLoading && videoSrc && (
                    <video
                      ref={videoRef}
                      controls
                      controlsList="nodownload"
                      disablePictureInPicture
                      preload="metadata"
                      width="100%"
                      onEnded={handleVideoEnd}
                    >
                      <source src={videoSrc} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  )}
                  {!videoLoading && !videoSrc && (
                    <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>
                      No se pudo cargar el vídeo.
                    </Typography>
                  )}
                </>
              )
            }
            {
              showCompletionStep &&
              <Box sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
                <Typography variant="h4" gutterBottom>
                  ¡Enhorabuena!
                </Typography>
                <CheckCircle color="success" sx={{ width: 60, height: 60, mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Has completado todos los tutoriales.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate("/")}
                  sx={{ mt: 3 }}
                >
                  Volver al inicio
                </Button>
              </Box>
            }
          </Box>
          {
            !showCompletionStep &&
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="contained"
                onClick={handlePrevious}
                sx={{visibility: currentVideoIndex === 0 ? 'hidden' : 'visible'}}
              >
                Anterior
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!progress[currentVideoIndex]}
              >
                Siguiente
              </Button>
            </Box>
          }
        </Box>
      </Paper>
    </Container>
  );
};

export default Tutorials;
